import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Newspaper, Mail } from "lucide-react";
import api from "@/lib/api";
import { safeJsonLd } from "@/lib/safeJson";
import SmoothScroll from "@/components/site/SmoothScroll";
import Header from "@/components/site/Header";
import TopMarquee from "@/components/site/TopMarquee";
import Footer from "@/components/site/Footer";
import { useLang } from "@/context/LangContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function schemaFor(author) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: author.name,
    jobTitle: author.designation || "Journalist",
    image: author.photo,
    description: author.bio,
    worksFor: {
      "@type": "NewsMediaOrganization",
      name: "Denva Post",
      alternateName: "देनवा पोस्ट",
    },
    url: `${BACKEND?.replace(/\/$/, "")}/author/${author.id}`,
  };
}

export default function AuthorPage() {
  const { id } = useParams();
  const { t, isEn } = useLang();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null);
    setNotFound(false);
    api
      .get(`/public/authors/${id}`)
      .then((r) => {
        setData(r.data);
        document.title = `${r.data.name} — ${isEn ? "Denva Post" : "देनवा पोस्ट"}`;
      })
      .catch(() => setNotFound(true));
  }, [id, isEn]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F4F4F0] flex flex-col items-center justify-center font-sans-dev px-4 text-center">
        <div className="font-serif-dev text-5xl font-black mb-4">404</div>
        <p className="text-[#4A4A4A] mb-6">{isEn ? "Author not found" : "लेखक नहीं मिला"}</p>
        <Link to="/" className="pill-btn text-[12px]" data-testid="author-back-home">
          {t("go_home")}
        </Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#F4F4F0] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D92525]" />
      </div>
    );
  }

  return (
    <SmoothScroll>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(schemaFor(data)) }}
      />
      <div className="App font-sans-dev" data-testid="author-page">
        <TopMarquee />
        <Header />
        <div className="max-w-[1080px] mx-auto px-4 md:px-8 py-10 md:py-16">
          <Link
            to="/"
            data-testid="author-back"
            className="inline-flex items-center gap-2 text-sm text-[#4A4A4A] hover:text-[#D92525] mb-8"
          >
            <ArrowLeft size={16} /> {t("all_news")}
          </Link>

          {/* Author header */}
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-8 border-b-2 border-[#0F0F0F]/80 pb-10">
            <img
              src={data.photo}
              alt={data.name}
              className="w-28 h-28 md:w-32 md:h-32 rounded-full object-cover border-4 border-[#0F0F0F] bg-white shrink-0"
              data-testid="author-photo"
            />
            <div className="min-w-0 flex-1">
              <div className="eyebrow text-[#D92525] mb-1.5">
                {t("author_page_title")} · Denva Post
              </div>
              <motion.h1
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                data-testid="author-name"
                className="font-serif-dev font-black text-[32px] sm:text-[44px] md:text-[52px] leading-[1.05] text-[#0F0F0F]"
              >
                {data.name}
              </motion.h1>
              {data.designation && (
                <div className="mt-2 text-[13px] uppercase tracking-[0.18em] text-[#4A4A4A]">
                  {data.designation}
                </div>
              )}
              {data.bio && (
                <p className="mt-4 font-serif-dev text-[17px] md:text-[19px] leading-relaxed text-[#1a1a1a] max-w-2xl">
                  {data.bio}
                </p>
              )}
              <div className="mt-5 inline-flex items-center gap-4 text-[12px] uppercase tracking-[0.18em] text-[#4A4A4A]">
                <span className="flex items-center gap-1.5" data-testid="author-count">
                  <Newspaper size={14} /> {data.articles_count} {t("total_articles")}
                </span>
              </div>
            </div>
          </div>

          {/* Articles list */}
          <h2 className="mt-10 mb-6 font-serif-dev font-black text-[24px] md:text-[28px]">
            {t("articles_by")} <span className="text-[#D92525]">{data.name}</span>
          </h2>

          {data.articles.length === 0 ? (
            <div className="text-center py-16 text-[#4A4A4A] font-serif-dev">
              {t("no_articles")}
            </div>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
              data-testid="author-articles"
            >
              {data.articles.map((a) => (
                <Link
                  key={a.id}
                  to={`/news/${a.slug}`}
                  data-testid={`author-article-${a.id}`}
                  className="group block"
                >
                  <div className="aspect-[16/10] overflow-hidden border border-[#0F0F0F] bg-gray-100 mb-3">
                    <img
                      src={a.img}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="eyebrow text-[#D92525]">
                    {a.kicker} · {a.time}
                  </div>
                  <h3 className="mt-1.5 font-serif-dev font-bold text-[19px] leading-tight group-hover:text-[#D92525] transition-colors">
                    {a.title}
                  </h3>
                  {a.summary && (
                    <p className="mt-2 text-sm text-[#4A4A4A] line-clamp-2">{a.summary}</p>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
        <Footer />
      </div>
    </SmoothScroll>
  );
}
